/**
 * Graphics Notes — the order-scoped log of customer graphic requests and the
 * designer <-> graphics-team conversation, persisted in SHARED storage so the
 * same notes surface in Skafer and Konstner (Pete 2026-11-04 directive).
 *
 * Data contract (live prod, verified 2026-11-04; full doc:
 * docs/contracts/graphics-notes.md):
 *  - Notes are `customerInteraction` rows on the CUSTOMER:
 *      GET/POST /api/v3/fjell/customer/<customerId>/customerInteraction/
 *    A graphics note is category='GRAPHICS'; a pasted customer email is
 *    interactionType='ARTWORK_EMAIL', channel='EMAIL', direction='INBOUND';
 *    an internal designer note is interactionType='NOTE_ADDED',
 *    direction='INTERNAL'. `orderId` links the note to the ski order so both
 *    apps can filter to one order.
 *  - Images are `orderFile` uploads with fileCode='GNOTE' on the ORDER:
 *      POST /api/v3/fjell/order/<orderId>/orderFile/upload
 *      {filename, fileContent(base64), fileCode:'GNOTE', file}
 *    Listed via GET /api/v3/fjell/order/<orderId>/orderFile/ (filter fileCode GNOTE).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress,
  Divider, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import type { ApiFn } from './ProposalPayments';

type RecordData = Record<string, any>;

const GRAPHICS_CATEGORY = 'GRAPHICS';
const GNOTE_FILE_CODE = 'GNOTE';

function items(body: any): RecordData[] { return Array.isArray(body) ? body : body?.items || []; }

export interface GraphicsNoteView {
  id: number | string;
  interactionType: string;
  direction: string;
  channel: string | null;
  summary: string | null;
  notes: string | null;
  occurredAt: string | null;
  createdAt: string | null;
  orderId: number | null;
  authorEmail: string | null;
}

/** Map a live customerInteraction row to the display shape. */
export function interactionToView(row: RecordData): GraphicsNoteView {
  return {
    id: row.id,
    interactionType: row.interactionType || 'NOTE_ADDED',
    direction: row.direction || 'INTERNAL',
    channel: row.channel ?? null,
    summary: row.summary ?? null,
    notes: row.notes ?? '',
    occurredAt: row.occurredAt || row.createdAt || null,
    createdAt: row.createdAt || null,
    orderId: row.orderId ?? null,
    authorEmail: row.createdBy?.email || row.rep?.email || null,
  };
}

export interface GraphicsImageView {
  id: number | string;
  filename: string;
  mediaLink: string | null;
  uploadedAt: string | null;
  orderId: number | null;
}

export function fileToView(row: RecordData): GraphicsImageView {
  const meta = row.metadata || {};
  return {
    id: row.id,
    filename: row.name || `file ${row.id}`,
    mediaLink: meta.mediaLink || meta.pathDisplay || null,
    uploadedAt: row.createdAt || null,
    orderId: row.orderId ?? null,
  };
}

/** Build the POST body for a new graphics-note interaction row. */
export function notePayload(opts: {
  customerId: number; orderId: number | null; mode: 'customer' | 'internal';
  text: string; createdById: number; now?: Date;
}): RecordData {
  const when = (opts.now || new Date()).toISOString();
  return {
    customerId: opts.customerId,
    orderId: opts.orderId,
    category: GRAPHICS_CATEGORY,
    interactionType: opts.mode === 'customer' ? 'ARTWORK_EMAIL' : 'NOTE_ADDED',
    channel: opts.mode === 'customer' ? 'EMAIL' : null,
    direction: opts.mode === 'customer' ? 'INBOUND' : 'INTERNAL',
    occurredAt: when,
    summary: opts.text.trim().slice(0, 120),
    notes: opts.text,
    sourceType: opts.orderId != null ? 'Order' : null,
    sourceId: opts.orderId != null ? String(opts.orderId) : null,
    createdById: opts.createdById,
  };
}

export type NoteMode = 'customer' | 'internal';

export function GraphicsNotes({ customer, activeOrder, token, api, userEmail }: {
  customer: RecordData | null;
  activeOrder: RecordData | null;
  token: string;
  api: ApiFn;
  userEmail: string;
}) {
  const [rows, setRows] = useState<GraphicsNoteView[]>([]);
  const [images, setImages] = useState<GraphicsImageView[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<NoteMode>('customer');
  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [authorId, setAuthorId] = useState<number | null>(null);
  const [sendingIntake, setSendingIntake] = useState(false);
  const [intakeResult, setIntakeResult] = useState<string | null>(null);

  const customerId = customer ? Number(customer.id ?? customer.key?.pk) : null;
  const orderId = activeOrder ? Number(activeOrder.id ?? activeOrder.key?.pk) : null;

  const reload = useCallback(async () => {
    if (customerId == null) return;
    setLoading(true); setError(null);
    try {
      const body = await api(`/api/v3/fjell/customer/${customerId}/customerInteraction/`, token);
      const views = items(body).filter((r) => r.category === GRAPHICS_CATEGORY).map(interactionToView);
      views.sort((a, b) => String(b.occurredAt || '').localeCompare(String(a.occurredAt || '')));
      setRows(views);
      if (orderId != null) {
        const fbody = await api(`/api/v3/fjell/order/${orderId}/orderFile/`, token);
        setImages(items(fbody).filter((r) => r.fileCode === GNOTE_FILE_CODE).map(fileToView));
      } else setImages([]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load graphics notes');
    } finally { setLoading(false); }
  }, [api, token, customerId, orderId]);

  useEffect(() => { setRows([]); setImages([]); void reload(); }, [reload]);

  // Resolve the signed-in Wagner user's id once (createdById is required).
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const body = await api('/api/v3/fjell/user/', token);
        const me = items(body).find((u) => (u.email || '').toLowerCase() === (userEmail || '').toLowerCase());
        if (!dead) setAuthorId(me ? Number(me.id) : null);
      } catch { /* resolution is best-effort; save falls back to admin id 1 */ }
    })();
    return () => { dead = true; };
  }, [api, token, userEmail]);

  const canAttach = orderId != null;

  const onPickFiles = (list: FileList | null) => {
    if (!list) return;
    setPendingFiles((prev) => [...prev, ...Array.from(list)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const readFileBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      resolve(s.includes(',') ? s.slice(s.indexOf(',') + 1) : s);
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

  const save = async () => {
    if (!draft.trim() || customerId == null || saving) return;
    setSaving(true); setError(null);
    try {
      if (canAttach) {
        for (const file of pendingFiles) {
          const base64 = await readFileBase64(file);
          await api(`/api/v3/fjell/order/${orderId}/orderFile/upload`, token, {
            method: 'POST',
            body: JSON.stringify({ filename: file.name, fileContent: base64, fileCode: GNOTE_FILE_CODE, file: file.name }),
          });
        }
      }
      const body = notePayload({
        customerId, orderId, mode, text: draft,
        createdById: authorId ?? 1,
      });
      await api('/api/v3/fjell/customer/' + customerId + '/customerInteraction/', token, {
        method: 'POST', body: JSON.stringify(body),
      });
      setDraft(''); setPendingFiles([]);
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to save the note');
    } finally { setSaving(false); }
  };

  const sendIntakeForm = async () => {
    if (orderId == null || sendingIntake) return;
    if (!window.confirm('Send the graphics-ideas form email to this customer now? This emails the customer.')) return;
    setSendingIntake(true); setError(null); setIntakeResult(null);
    try {
      const body = await api(`/api/v3/fjell/skafer/order/${orderId}/sendGraphicsIntake`, token, {
        method: 'POST', body: JSON.stringify({}),
      }) as RecordData;
      setIntakeResult(`Intake email sent to ${body.to}. The form link is live for this order.`);
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to send the intake email');
    } finally { setSendingIntake(false); }
  };

  const typeLabel = useMemo(() => ({
    ARTWORK_EMAIL: 'Customer email', GRAPHICS_EMAIL: 'Graphics email',
    NOTE_ADDED: 'Internal note', PROOF_SENT: 'Proof sent', PROOF_APPROVED: 'Proof approved',
  } as Record<string, string>), []);

  if (!customer) return null;

  return (
    <Stack spacing={2}>
      <Card>
        <CardHeader title="Add graphics note" subheader={orderId != null ? `For ${activeOrder?.name || `order #${orderId}`}` : 'No order selected — attaches to the customer'} />
        <CardContent>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, v: NoteMode) => v && setMode(v)} sx={{ mb: 1.5 }}>
            <ToggleButton value="customer">Customer email</ToggleButton>
            <ToggleButton value="internal">Internal note</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth multiline minRows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={mode === 'customer'
              ? "Paste the customer's graphic email here — ideas, layout, references"
              : 'Note for the graphic designer / ski design conversation'}
          />
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
          {intakeResult && <Alert severity="success" sx={{ mt: 1.5 }}>{intakeResult}</Alert>}
          {orderId != null && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Button variant="contained" color="secondary" onClick={() => { void sendIntakeForm(); }} disabled={sendingIntake}>
                {sendingIntake ? <CircularProgress size={20} /> : 'Send intake form to customer'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Emails the customer their personal graphics-ideas page. You press send every time.
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Button
              variant="outlined" component="label" disabled={!canAttach}
              title={canAttach ? 'Attach reference images to the order' : 'Select an order first'}>
              Attach images
              <input type="file" multiple hidden ref={fileInputRef} onChange={(e) => onPickFiles(e.target.files)} />
            </Button>
            {pendingFiles.length > 0 && <Typography variant="caption" color="text.secondary">{pendingFiles.length} image(s) attached</Typography>}
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" onClick={() => { void save(); }} disabled={saving || !draft.trim()}>
              {saving ? <CircularProgress size={20} /> : 'Save note'}
            </Button>
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {canAttach && images.length > 0 && (
        <Card><CardHeader title="Reference images" subheader="Stored on the order (fileCode GNOTE) — shared with Konstner" />
          <CardContent>
            <Stack spacing={0.5}>
              {images.map((img) => (
                <Stack key={String(img.id)} direction="row" spacing={1} alignItems="baseline">
                  {img.mediaLink
                    ? <a href={img.mediaLink} target="_blank" rel="noreferrer">{img.filename}</a>
                    : <Typography>{img.filename}</Typography>}
                  {img.uploadedAt && <Typography variant="caption" color="text.secondary">{img.uploadedAt.slice(0, 10)}</Typography>}
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader title="Notes timeline" subheader="Newest first — shared with Konstner" action={loading ? <CircularProgress size={20} /> : null} />
        <CardContent>
          {rows.length === 0 && !loading && <Typography color="text.secondary">No graphics notes yet. Paste the customer&apos;s email above to start the record.</Typography>}
          <Stack spacing={2} divider={<Divider flexItem />}>
            {rows.map((n) => (
              <Box key={String(n.id)}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color={n.direction === 'INBOUND' ? 'primary' : 'default'} label={typeLabel[n.interactionType] || n.interactionType} />
                  {n.orderId != null && <Chip size="small" variant="outlined" label={`order #${n.orderId}`} />}
                  <Typography variant="caption" color="text.secondary">
                    {n.occurredAt ? n.occurredAt.slice(0, 16).replace('T', ' ') : ''}
                    {n.authorEmail ? ` · ${n.authorEmail}` : ''}
                  </Typography>
                </Stack>
                {n.summary && n.summary !== (n.notes || '').slice(0, 120) && <Typography fontWeight={650}>{n.summary}</Typography>}
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{n.notes}</Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}